import { useEffect } from "react";
import { useLocation } from "@/lib/router-compat";
import { readDraft } from "@/hooks/useFormDraftCapture";

const DRAFT_FIELD_MAP: Record<string, RegExp[]> = {
  name: [/^name$/i, /(^|[-_])name([-_]|$)/i],
  phone: [/^phone$/i, /phone|mobile|tel/i],
  email: [/^email$/i, /email/i],
  zip: [/^zip$/i, /\bzip\b|postal/i],
  linear_feet: [/feet|linear[-_]?feet|footage|length/i],
};

function classifyByAttrs(el: HTMLInputElement | HTMLTextAreaElement): string | null {
  const ac = (el.autocomplete || "").toLowerCase();
  const nm = `${el.name || ""} ${el.id || ""}`.toLowerCase();
  if (ac === "name" || /(^|[-_])name([-_]|$)/.test(nm)) return "name";
  if (ac === "tel" || el.type === "tel" || /phone|mobile|tel/.test(nm)) return "phone";
  if (ac === "email" || el.type === "email" || /email/.test(nm)) return "email";
  if (ac === "postal-code" || /\bzip\b|postal/.test(nm)) return "zip";
  if (/\b(feet|linear[-_]?feet|footage|length)\b/.test(nm)) return "linear_feet";
  return null;
}

function setNativeValue(el: HTMLInputElement | HTMLTextAreaElement, value: string) {
  const proto = el instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
  const setter = Object.getOwnPropertyDescriptor(proto, "value")?.set;
  if (setter) {
    setter.call(el, value);
  } else {
    el.value = value;
  }
  el.dispatchEvent(new Event("input", { bubbles: true }));
  el.dispatchEvent(new Event("change", { bubbles: true }));
}

function findForm(source?: string): HTMLFormElement | null {
  // Prefer a form whose data-capture-source matches; fall back to first capture form.
  const forms = Array.from(document.querySelectorAll<HTMLFormElement>("form[data-capture-draft]"));
  if (!forms.length) return null;
  if (source) {
    const exact = forms.find((f) => f.dataset['captureSource'] === source);
    if (exact) return exact;
    const partial = forms.find((f) => f.dataset['captureSource']?.startsWith(source.split(":")[0] ?? ""));
    if (partial) return partial;
  }
  return forms[0] || null;
}

function applyResume(sourceOverride?: string | null): boolean {
  const draft = readDraft();
  const preferredSource = sourceOverride || draft.source;
  const form = findForm(preferredSource);
  if (!form) return false;

  const fields = Array.from(form.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>("input, textarea"));
  let firstEmptyTouched: HTMLElement | null = null;

  for (const el of fields) {
    const key = classifyByAttrs(el);
    if (!key) continue;
    const value = (draft as Record<string, unknown>)[key];
    if (value === undefined || value === null || value === "") continue;
    if (el.value && el.value.trim().length > 0) continue; // don't clobber user input
    setNativeValue(el, String(value ?? ''));
  }

  // Focus the first still-empty captured field (where they likely left off)
  for (const el of fields) {
    const key = classifyByAttrs(el);
    if (!key) continue;
    if (!el.value || el.value.trim() === "") {
      firstEmptyTouched = el;
      break;
    }
  }

  // Scroll the form into view
  form.scrollIntoView({ behavior: "smooth", block: "center" });
  if (firstEmptyTouched) {
    setTimeout(() => (firstEmptyTouched as HTMLInputElement).focus({ preventScroll: true }), 350);
  }

  void import("@/lib/analytics").then(({ trackEvent }) =>
    trackEvent("draft_followup_resumed", {
      source: draft.source,
      had_email: Boolean(draft.email),
    }),
  );

  return true;
}

/**
 * Global hook mounted in Layout. When a route has `?resume=1` (e.g., from the
 * "Finish quote" toast button or the follow-up email link), wait for the page
 * to mount, then prefill any matching <form data-capture-draft> with the
 * stored draft values and focus the first empty captured field.
 */
export function useDraftResume() {
  const { search, pathname } = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(search);
    if (params.get("resume") !== "1") return;
    const sourceHint = params.get("src");

    let attempts = 0;
    const maxAttempts = 12; // ~3s total
    const tick = () => {
      attempts += 1;
      if (applyResume(sourceHint)) {
        // Clean the URL so refreshing doesn't re-trigger
        const url = new URL(window.location.href);
        url.searchParams.delete("resume");
        url.searchParams.delete("src");
        url.searchParams.delete("field");
        window.history.replaceState({}, "", url.toString());
        return;
      }
      if (attempts < maxAttempts) {
        setTimeout(tick, 250);
      }
    };
    tick();
  }, [search, pathname]);
}
